import { useEffect, useState, useRef, useCallback } from 'react';

export interface FacebookCookiesResult {
  fbc: string | null;
  fbp: string | null;
  ready: boolean;
}

export const getCookie = (name: string): string | undefined => {
  if (typeof document === 'undefined') {
    return undefined;
  }
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift();
  }
  return undefined;
};

const getFbclidFromUrl = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  const params = new URLSearchParams(window.location.search);
  return params.get('fbclid');
};

const constructFbc = (fbclid: string): string => {
  const timestamp = Date.now();
  return `fb.1.${timestamp}.${fbclid}`;
};

/**
 * Hook pour gérer la récupération des cookies Facebook avec gestion de la race condition.
 *
 * - Lit immédiatement les cookies _fbc et _fbp
 * - Si les deux existent, retourne immédiatement ready=true
 * - Sinon, poll toutes les 200ms pendant max 3s
 * - À 3s, si _fbc n'existe pas mais fbclid est présent, construit _fbc manuellement
 * - ready passe à true quand les deux existent OU après 3s
 */
export const useFacebookCookies = (): FacebookCookiesResult => {
  const [fbc, setFbc] = useState<string | null>(null);
  const [fbp, setFbp] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);

  const readCookies = useCallback(() => {
    const currentFbc = getCookie('_fbc') || null;
    const currentFbp = getCookie('_fbp') || null;
    return { fbc: currentFbc, fbp: currentFbp };
  }, []);

  const cleanup = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    const { fbc: initialFbc, fbp: initialFbp } = readCookies();
    const fbclid = getFbclidFromUrl();

    // Cas 1 : Les deux cookies existent déjà
    if (initialFbc && initialFbp) {
      setFbc(initialFbc);
      setFbp(initialFbp);
      setReady(true);
      return;
    }

    // Cas 2 : Pas de fbclid et pas de cookies → ready immédiatement
    if (!fbclid && !initialFbc && !initialFbp) {
      setFbc(null);
      setFbp(null);
      setReady(true);
      return;
    }

    // Cas 3 : fbclid ou cookies partiels → polling jusqu'à 3s
    setFbc(initialFbc);
    setFbp(initialFbp);
    startTimeRef.current = Date.now();

    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const { fbc: currentFbc, fbp: currentFbp } = readCookies();

      if (currentFbc && currentFbp) {
        setFbc(currentFbc);
        setFbp(currentFbp);
        setReady(true);
        cleanup();
        return;
      }

      if (elapsed >= 3000) {
        let finalFbc = currentFbc;
        if (!currentFbc && fbclid) {
          finalFbc = constructFbc(fbclid);
        }
        setFbc(finalFbc);
        setFbp(currentFbp);
        setReady(true);
        cleanup();
        return;
      }

      setFbc(currentFbc);
      setFbp(currentFbp);
    }, 200);

    return cleanup;
  }, [readCookies, cleanup]);

  return { fbc, fbp, ready };
};

export default useFacebookCookies;
